import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { PhoneCall } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export const RCP_STATUS = {
  not_connected: ["Not connected", "bg-slate-100 text-slate-700"],
  choose_plan: ["Choose a plan", "bg-slate-100 text-slate-700"],
  payment_pending: ["Payment pending", "bg-amber-100 text-amber-800"],
  setup_incomplete: ["Setup incomplete", "bg-amber-100 text-amber-800"],
  ready_to_test: ["Ready to activate", "bg-sky-100 text-sky-800"],
  trial: ["Trial", "bg-violet-100 text-violet-800"],
  active: ["Active", "bg-emerald-100 text-emerald-800"],
  renewal_due: ["Renewal due", "bg-amber-100 text-amber-800"],
  grace: ["In grace period", "bg-orange-100 text-orange-800"],
  expired: ["Expired", "bg-rose-100 text-rose-800"],
  trial_expired: ["Trial expired", "bg-rose-100 text-rose-800"],
  suspended: ["Suspended", "bg-rose-100 text-rose-800"],
  limit: ["Usage limit reached", "bg-rose-100 text-rose-800"],
  error: ["Error", "bg-rose-100 text-rose-800"],
};
const RANK = ["error", "active", "ready_to_test", "setup_incomplete", "not_connected"];

export function StatusPill({ s, testid }) {
  const [l, c] = RCP_STATUS[s] || RCP_STATUS.not_connected;
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${c}`} data-testid={testid}>{l}</span>;
}

export function cardStatus(ent, setup) {
  if (!ent) return "not_connected";
  if (["expired", "trial_expired", "suspended", "payment_pending", "grace"].includes(ent.status)) return ent.status;
  if (!ent.ok) return "choose_plan";
  if (ent.minutes_remaining <= 0) return "limit";
  if (ent.renewal_due) return "renewal_due";
  const b = setup ? (RANK.find((r) => setup.branches.some((x) => x.status === r)) || "not_connected") : "not_connected";
  return b === "active" || b === "error" ? b : b === "ready_to_test" ? "ready_to_test" : ent.kind === "trial" && b !== "setup_incomplete" ? "trial" : "setup_incomplete";
}

export function expiryText(ent) {
  if (!ent?.ok && !ent?.period_end) return "";
  if (ent.kind === "trial") return `Trial ends: ${ent.expiry_label || "Expiry not set"}`;
  return ent.period_end ? `${ent.status === "grace" ? "Grace ends" : "Renews manually by"}: ${new Date(ent.grace_end || ent.period_end).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}` : "";
}

export function ConnectReceptionistCard() {
  const nav = useNavigate();
  const { data: ent } = useQuery({ queryKey: ["rcp-ent"], queryFn: async () => (await api.get("/receptionist/billing/status")).data, retry: false });
  const { data: setup } = useQuery({ queryKey: ["rcp-setup"], queryFn: async () => (await api.get("/receptionist/setup")).data, retry: false });
  const st = cardStatus(ent, setup);
  const nums = (setup?.branches || []).flatMap((b) => b.numbers.map((n) => n.number));
  const manage = ent?.ok || ["expired", "trial_expired", "grace", "suspended"].includes(ent?.status);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid="connect-rcp-card">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 text-violet-700"><PhoneCall className="h-5 w-5" /></span>
        <div>
          <p className="font-semibold">AI Receptionist <StatusPill s={st} testid="connect-rcp-status" /></p>
          <p className="text-xs text-slate-500">Never miss a customer call. Let an AI receptionist answer calls, explain your services, and help customers request appointments.</p>
          {ent?.plan && <p className="mt-1 text-xs text-slate-600" data-testid="connect-rcp-usage">{ent.plan.name}{ent.kind === "trial" ? " (trial)" : ""} · {ent.minutes_used}/{ent.minutes_included} min used · {ent.minutes_remaining} left{nums.length ? ` · ${nums.join(", ")}` : " · No number assigned"}</p>}
          {expiryText(ent) && <p className="text-xs text-slate-500" data-testid="connect-rcp-expiry">{expiryText(ent)}</p>}
        </div>
      </div>
      <Button size="sm" onClick={() => nav(manage ? "/settings/ai-receptionist" : "/settings/ai-receptionist/plans")} data-testid="connect-rcp-open">{manage ? "Manage AI Receptionist" : "Connect AI Receptionist"}</Button>
    </div>
  );
}
